import { DataTable } from '@/components'
import { ALL_WORKSHOPS_QUERY } from '@/modules/vehicles/queries'
import { useWorkshopMutations } from '@/modules/vehicles/fleet/mutations.hooks'
import type { WorkshopNode } from '@/modules/vehicles/types.graphql'
import { getNodes } from '@/utils/graphql'
import { useOnErrorAuto, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useQuery } from '@apollo/client'
import { createColumnHelper } from '@tanstack/react-table'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import { AddButton, Section } from '../common'
import type { WorkshopForm } from './workshop-form'
import { WorkshopPrompt } from './workshop-prompt'
import { WorkshopRowActions } from './workshop-row-actions'

const column = createColumnHelper<WorkshopNode>()

export const WorkshopSettings: React.FC = () => {
  const translations = useTranslate({
    workshops: 'settings.workshops',
    addWorkshop: 'settings.add-workshop',
    info: 'settings.info.workshops',

    name: 'common.name',
    address: 'common.address',
    phone: 'common.phone',
    email: 'common.email',
    services: 'vehicles.vehicle-services',
    noResults: 'common.no-results',
  })

  const addPrompt = usePrompt()
  const onErrorAuto = useOnErrorAuto()
  const { createWorkshop, loading: mutating } = useWorkshopMutations()

  const { hasPermissions } = usePermissions()
  const canAdd = hasPermissions(
    PERMISSIONS.vehicles.add.vehicleexpiration,
    true
  )

  const { data, loading } = useQuery(ALL_WORKSHOPS_QUERY, {
    variables: { orderBy: 'name' },
    onError: onErrorAuto(),
  })

  const workshops = useMemo(() => getNodes(data?.allWorkshops), [data])

  async function handleCreate() {
    const { data } = await addPrompt<WorkshopForm | null>(resolve => (
      <WorkshopPrompt onSubmit={resolve} />
    ))
    if (!data) return

    await createWorkshop({ variables: { input: data } })
  }

  const columns = useMemo(
    () => [
      column.accessor('name', {
        header: translations.name,
        enableSorting: true,
        cell: info => (
          <span className="font-medium text-stone-900">{info.getValue()}</span>
        ),
      }),
      column.accessor('address', {
        header: translations.address,
        enableSorting: true,
        cell: info => info.getValue() || '-',
      }),
      column.accessor('phone', {
        header: translations.phone,
        cell: info => info.getValue() || '-',
      }),
      column.accessor('email', {
        header: translations.email,
        cell(info) {
          const email = info.getValue()
          if (!email) return '-'
          return (
            <a className="text-inherit" href={`mailto:${email}`}>
              {email}
            </a>
          )
        },
      }),
      column.accessor('servicePlansCount', {
        header: translations.services,
        enableSorting: true,
        meta: { narrow: true, end: true },
      }),
      column.display({
        id: 'actions',
        meta: { narrow: true },
        cell: info => <WorkshopRowActions workshop={info.row.original} />,
      }),
    ],
    [
      translations.address,
      translations.email,
      translations.name,
      translations.phone,
      translations.services,
    ]
  )

  return (
    <Section
      header={translations.workshops}
      loading={loading || mutating}
      actions={
        canAdd && (
          <AddButton
            addText={translations.addWorkshop}
            onClick={() => void handleCreate()}
          />
        )
      }
    >
      <p className="mt-0 mb-4 text-sm text-stone-500">{translations.info}</p>

      <DataTable
        columns={columns}
        data={workshops}
        loading={loading && workshops.length === 0}
        initialSort={[{ id: 'name', desc: false }]}
        noDataText={translations.noResults}
      />
    </Section>
  )
}
